"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.generateSupplierBills = void 0;
const helpers_1 = require("../../helpers");
const getLatestOrderLineVersion_1 = require("./getLatestOrderLineVersion");
const getOrderLineAccountancyDetails_1 = require("./getOrderLineAccountancyDetails");
const getOrderLineCurrency_1 = require("./getOrderLineCurrency");
const getOrderLineTotal_1 = require("./getOrderLineTotal");
const getPreviousOrderLineVersion_1 = require("./getPreviousOrderLineVersion");
const uuid_1 = require("uuid");
const generateSupplierBills = async (supabase, orderId, userId, supplierId) => {
    // And we can run queries in the context of our authenticated user
    const { data, error } = await supabase
        .from("orders")
        .select(`*, 
    client:user_profiles!client_id(*), 
    order_lines!id(*, 
      versions:order_line_versions!order_line_id(
        *,
        regions(id, name, slug, country_id),
        order_lines!linked_line_id(*, 
          versions:order_line_versions!order_line_id
          (*)
        )
      )
    ),
    created_by_user:created_by(id, first_name, last_name)`)
        .eq("id", orderId)
        .single();
    if (error)
        throw error;
    const order = data;
    // get the order lines (ONLY CONFIRMED OR PAID ONES)
    const orderLines = order?.order_lines?.filter((x) => {
        return [helpers_1.OrderLineStatus.CONFIRMED, helpers_1.OrderLineStatus.CONFIRMED_PAID].includes(x.status);
    });
    if (orderLines.length === 0)
        throw new Error("No order lines found");
    let lines = [];
    let idx = 0;
    for (const line of orderLines) {
        if (!line.versions)
            throw new Error("No versions found for order line");
        const latest_version = (0, getLatestOrderLineVersion_1.getLatestOrderLineVersion)(line.versions);
        const previous_version = (0, getPreviousOrderLineVersion_1.getPreviousOrderLineVersion)(line.versions);
        const order_line_total = (0, getOrderLineTotal_1.getOrderLineTotal)(line);
        if (!latest_version) {
            // skip this item in loop if no latest version
            continue;
        }
        const { data: dueDate, error: dueDateError } = await supabase
            .rpc("get_order_line_due_date", {
            p_order_line_id: line.id,
        })
            .maybeSingle();
        if (dueDateError) {
            console.error("Error getting due date for order line", line.id, dueDateError);
        }
        // TODO - Using the new 'mega' query to get the payment terms add this to the order_lines accountancy details so that i can correctly calculate the due date for the bill
        let amount_paid = 0;
        // if the latest version matches the order line total then we can set amount paid to the order line total. If there is a difference then we need to calculate the difference use the difference as the amount paid
        if (latest_version?.amount_paid === order_line_total) {
            amount_paid = order_line_total;
        }
        else {
            amount_paid =
                (latest_version?.amount_paid || 0) -
                    (previous_version?.amount_paid || 0);
        }
        const items = (0, getOrderLineAccountancyDetails_1.getOrderLineAccountancyDetails)(line, dueDate, amount_paid, idx);
        lines = [...lines, ...items];
        idx++;
    }
    // group each line by supplier_id then the group_id
    let groupedLines = lines.reduce((acc, line) => {
        const supplier = acc.find((s) => s.supplier_id === line.supplier_id);
        if (supplier) {
            const group = supplier.groups.find((g) => g.group_id === line.group_id);
            if (group) {
                group.lines.push(line);
            }
            else {
                supplier.groups.push({
                    group_id: line.group_id,
                    group_name: line.group_name,
                    lines: [line],
                });
            }
        }
        else {
            acc.push({
                supplier_id: line.supplier_id,
                groups: [
                    {
                        group_id: line.group_id,
                        group_name: line.group_name,
                        lines: [line],
                    },
                ],
            });
        }
        return acc;
    }, []);
    const currency = (0, getOrderLineCurrency_1.getOrderLineCurrency)(orderLines[0]) || "";
    const supplier_outstanding = [];
    if (supplierId) {
        groupedLines = groupedLines.filter((s) => s.supplier_id === supplierId);
    }
    // create a bill object for each supplier group
    const bills = groupedLines.map((supplier) => {
        const transaction_id = (0, uuid_1.v4)();
        // for each group in the supplier create a bill
        const notes = supplier.groups.map((group) => {
            return group.group_name;
        });
        const total = supplier.groups.reduce((acc, group) => {
            const groupTotal = group.lines.reduce((acc, line) => {
                return acc + line.cost;
            }, 0);
            return acc + groupTotal;
        }, 0);
        const transaction_items = [];
        supplier.groups.forEach((group) => {
            group.lines.forEach((line) => {
                const transaction_item_id = (0, uuid_1.v4)();
                if (line.cost > 0) {
                    supplier_outstanding.push({
                        id: (0, uuid_1.v4)(),
                        supplier_id: supplier.supplier_id,
                        transaction_item_id: transaction_item_id,
                        amount: line.cost,
                        date_of_service: line.service_date,
                        data: line,
                    });
                    transaction_items.push({
                        id: transaction_item_id,
                        transaction_id: transaction_id,
                        order_id: orderId,
                        amount: line.cost,
                        direction: -1,
                        currency: currency,
                        name_id: line.supplier_id,
                        created_by: userId,
                        note: `${line.group_name}`,
                        account_id: (0, helpers_1.getCreditorAccountByCurrency)(currency, line.name),
                        order_line_id: line.order_line_id,
                        due_date: line.due_date,
                    });
                }
            });
            return transaction_items;
        });
        transaction_items.push({
            id: (0, uuid_1.v4)(),
            transaction_id: transaction_id,
            order_id: orderId,
            amount: total,
            direction: 1,
            currency: currency,
            name_id: supplier.supplier_id,
            created_by: userId,
            note: notes.join(", "),
            account_id: (0, helpers_1.getCostOfSalesAccountByCurrencySource)(currency, order.source),
        });
        return {
            id: transaction_id,
            type: "BILL",
            currency: currency,
            amount: total,
            note: `Bill${notes.length > 1 ? "s" : ""} for ${notes.join(", ")}`,
            name_id: supplier.supplier_id,
            created_by: userId,
            items: transaction_items,
        };
    });
    // Group up all promises
    const transactions_promise = [];
    const transaction_items_promise = [];
    const transaction_order_promise = [];
    const supplier_outstanding_promise = [];
    await bills.forEach(async (bill) => {
        // deep clone the bill object
        const transaction_orders = [];
        transaction_orders.push({
            transaction_id: bill.id,
            order_id: order.id,
        });
        const transaction = JSON.parse(JSON.stringify(bill));
        const transaction_items = transaction.items;
        delete transaction.items;
        transactions_promise.push(supabase.from("transactions").insert([transaction]));
        transaction_items_promise.push(supabase.from("transaction_items").insert(transaction_items).select());
        transaction_order_promise.push(supabase.from("transaction_orders").insert(transaction_orders));
    });
    supplier_outstanding_promise.push(supabase.from("supplier_payment_outstanding").insert(supplier_outstanding));
    await Promise.all(transactions_promise);
    await Promise.all(transaction_items_promise);
    await Promise.all(transaction_order_promise);
    await Promise.all(supplier_outstanding_promise);
    // TO DO:  Add Transactaion_Items for each Line Item thingy...
    return bills;
};
exports.generateSupplierBills = generateSupplierBills;
